import { useCallback, useEffect, useState, type ReactNode } from "react";
import {
  Navigate,
  NavLink,
  Route,
  Routes,
  useSearchParams,
} from "react-router-dom";

import { api, type Me } from "@/api";
import { Button } from "@/components/ui/button";
import { ErrorAlert } from "@/components/error-alert";
import { PageSkeleton } from "@/components/page-skeleton";
import { cn } from "@/lib/utils";
import { TodayPage } from "@/pages/today";
import { WeekPage } from "@/pages/week";
import { PlannerPage } from "@/pages/planner";
import { TemplatesPage } from "@/pages/templates";
import { RegimensPage } from "@/pages/regimens";
import { RoutinesPage } from "@/pages/routines";
import { WishlistPage } from "@/pages/wishlist";
import { SettingsPage } from "@/pages/settings";

const nav: { to: string; label: string }[] = [
  { to: "/today", label: "Today" },
  { to: "/planner", label: "Planner" },
  { to: "/week", label: "Retrospective" },
  { to: "/templates", label: "Templates" },
  { to: "/regimens", label: "Regimens" },
  { to: "/routines", label: "Routines" },
  { to: "/wishlist", label: "Wishlist" },
  { to: "/settings", label: "Settings" },
];

const authErrors: Record<string, string> = {
  calendar_required:
    "Google Calendar permission is required. Sign-in did not complete.",
  oauth_denied: "Sign-in was cancelled.",
  oauth_failed: "Sign-in failed. Try again.",
  not_configured: "Google sign-in is not configured on this server.",
};

function SignIn() {
  const [searchParams] = useSearchParams();
  const code = searchParams.get("error");
  const message = code
    ? (authErrors[code] ?? "Sign-in could not be completed.")
    : null;
  return (
    <main className="flex min-h-screen items-center justify-center px-4">
      <div className="w-full max-w-sm space-y-6 text-center">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Leno</h1>
          <p className="text-sm text-muted-foreground">
            Focus. Structure. Progress.
          </p>
        </div>
        {message && <ErrorAlert message={message} />}
        <Button asChild size="lg" className="w-full">
          <a href="/api/auth/google">Sign in with Google</a>
        </Button>
        <p className="text-xs text-muted-foreground">
          Sign-in also connects your Google Calendar, which Leno requires.
        </p>
      </div>
    </main>
  );
}

function Shell({
  me,
  onSignedOut,
  children,
}: {
  me: Me;
  onSignedOut: () => Promise<void>;
  children: ReactNode;
}) {
  const [error, setError] = useState<string | null>(null);
  const who = me.user?.name || me.user?.email || "";

  async function signOut() {
    setError(null);
    try {
      await api.logout();
      await onSignedOut();
    } catch (err) {
      setError(err instanceof Error ? err.message : "could not sign out");
    }
  }

  return (
    <div className="mx-auto min-w-0 max-w-6xl overflow-x-clip px-4 pb-16 pt-6">
      <header className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-6">
          <div>
            <div className="text-lg font-semibold tracking-tight">Leno</div>
            <div className="text-sm text-muted-foreground">
              Focus. Structure. Progress.
            </div>
          </div>
          <nav className="flex flex-wrap gap-1.5" aria-label="Main">
            {nav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    "rounded-full border px-3.5 py-1.5 text-sm transition-colors",
                    isActive
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border text-muted-foreground hover:text-foreground",
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
        <div className="flex items-center gap-3">
          <span
            className="hidden text-sm text-muted-foreground sm:inline"
            title={me.user?.email}
          >
            {who}
          </span>
          <Button variant="outline" size="sm" onClick={() => void signOut()}>
            Sign out
          </Button>
        </div>
      </header>
      {error && (
        <div className="mb-4">
          <ErrorAlert message={error} />
        </div>
      )}
      <main className="min-w-0">{children}</main>
    </div>
  );
}

export function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setMe(await api.me());
      setError(null);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "could not reach the server",
      );
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) {
    return (
      <main className="mx-auto max-w-sm px-4 pt-16">
        <ErrorAlert message={error} />
      </main>
    );
  }
  if (!me) {
    return (
      <main className="mx-auto max-w-6xl px-4 pt-6">
        <PageSkeleton />
      </main>
    );
  }
  if (!me.authenticated) {
    return <SignIn />;
  }

  return (
    <Shell me={me} onSignedOut={load}>
      <Routes>
        <Route path="/" element={<Navigate to="/today" replace />} />
        <Route path="/today" element={<TodayPage />} />
        <Route path="/planner" element={<PlannerPage />} />
        <Route path="/week" element={<WeekPage />} />
        <Route path="/templates" element={<TemplatesPage />} />
        <Route path="/regimens" element={<RegimensPage />} />
        <Route path="/routines" element={<RoutinesPage />} />
        <Route path="/wishlist" element={<WishlistPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="*" element={<Navigate to="/today" replace />} />
      </Routes>
    </Shell>
  );
}
